import {
  type InfiniteData,
  type QueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { API_ENDPOINTS } from 'Constants/env';
import {
  QUERY_KEY_FETCH_SHARED_ITEMS,
  SHARED_ITEMS_PAGE_LIMIT,
} from 'Modules/sharedItems/index.constants';
import type {
  FetchSharedItemsParams,
  SharedItem,
  SharedItemsResponse,
} from 'Modules/sharedItems/index.types';
import API from '~/api';

type SharedItemsInfiniteData = InfiniteData<SharedItemsResponse, string | undefined>;

/**
 * Fetches shared items for a conversation from the API.
 * Supports pagination through the cursor returned by the previous request.
 */
export const fetchSharedItems = async ({
  conversationId,
  cursor,
  limit = SHARED_ITEMS_PAGE_LIMIT,
}: FetchSharedItemsParams): Promise<SharedItemsResponse> => {
  const response = await API.get<SharedItemsResponse>(
    API_ENDPOINTS.ConversationSharedItems(conversationId, {
      ...(cursor ? { cursor } : {}),
      limit,
    })
  );

  return {
    sharedItems: response.data?.sharedItems ?? [],
    nextCursor: response.data?.nextCursor ?? null,
  };
};

/**
 * Invalidates cached shared items for a conversation so they are fetched again
 * with the latest data.
 *
 * If no conversation ID is provided, all shared items queries are invalidated.
 */
export const invalidateSharedItemsQueries = (
  queryClient: QueryClient,
  conversationId?: string
) => {
  void queryClient.invalidateQueries({
    queryKey: conversationId
      ? [QUERY_KEY_FETCH_SHARED_ITEMS, conversationId]
      : [QUERY_KEY_FETCH_SHARED_ITEMS],
  });
};

/**
 * Inserts or replaces a shared item in the React Query infinite-cache for a
 * conversation. Does not refetch. No-ops when the query has not been loaded yet.
 */
export const upsertSharedItemInQueryCache = (
  queryClient: QueryClient,
  conversationId: string,
  item: SharedItem
) => {
  queryClient.setQueryData<SharedItemsInfiniteData>(
    [QUERY_KEY_FETCH_SHARED_ITEMS, conversationId],
    (old) => {
      if (!old) {
        return old;
      }

      let found = false;
      const pages = old.pages.map((page) => {
        const index = page.sharedItems.findIndex(
          (sharedItem) => sharedItem.id === item.id
        );

        if (index === -1) {
          return page;
        }

        found = true;
        const sharedItems = [...page.sharedItems];
        sharedItems[index] = item;
        return { ...page, sharedItems };
      });

      if (found) {
        return { ...old, pages };
      }

      const [firstPage, ...restPages] = pages;
      if (!firstPage) {
        return {
          ...old,
          pages: [{ sharedItems: [item], nextCursor: null }],
        };
      }

      return {
        ...old,
        pages: [
          {
            ...firstPage,
            sharedItems: [item, ...firstPage.sharedItems],
          },
          ...restPages,
        ],
      };
    }
  );
};

/**
 * Removes a shared item from the React Query infinite-cache for a conversation.
 * Does not refetch. No-ops when the query has not been loaded yet.
 */
export const removeSharedItemFromQueryCache = (
  queryClient: QueryClient,
  conversationId: string,
  itemId: string
) => {
  queryClient.setQueryData<SharedItemsInfiniteData>(
    [QUERY_KEY_FETCH_SHARED_ITEMS, conversationId],
    (old) => {
      if (!old) {
        return old;
      }

      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          sharedItems: page.sharedItems.filter(
            (sharedItem) => sharedItem.id !== itemId
          ),
        })),
      };
    }
  );
};

export const useSharedItems = (conversationId?: string) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEY_FETCH_SHARED_ITEMS, conversationId],
    queryFn: ({ pageParam }) => {
      // The query is only enabled when a conversation ID is available.
      if (!conversationId) {
        return Promise.reject(new Error('useSharedItems: conversationId is required'));
      }

      return fetchSharedItems({ conversationId, cursor: pageParam });
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    initialPageParam: undefined as string | undefined,
    enabled: !!conversationId,
  });
};
